import { t } from '@lingui/core/macro'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useMyConversationSessions } from '../../api/conversations'
import {
  extractApiError,
  fetchMySessions,
  fetchSessionStatus,
  joinSession,
  leaveSession,
  requestRestart,
  signalReady,
  voteRestart
} from '../../api/conversations-api'
import { useConversationLogger } from '../../hooks/useConversationLogger'
import { initialRestartState, useSessionLiveKit } from '../../hooks/useSessionLiveKit'
import { NectarTasksAllDone } from '../NectarTasksAllDone'
import { SessionCall } from './SessionCall'
import { SessionLobby } from './SessionLobby'
import { ConversationCompleteStep } from './steps/ConversationCompleteStep'
import { ErrorStep } from './steps/ErrorStep'
import { WaitingForSessionStep } from './steps/WaitingForSessionStep'

type TaskInfo = {
  topic: string | null
  instruction: string | null
  participantInstructions?: Array<{ participantIndex: number; instructions: string }>
}

type EnterCallSession = {
  sessionId: string
  token: string
  session: {
    joinedCount: number
    requiredCount: number
    readyCount: number
    minDurationSec: number
    maxDurationSec: number
    callStartedAt: string | null
    inviteCode: string
  }
  task?: TaskInfo
}

type Phase =
  | { step: 'finding' }
  | {
      step: 'lobby'
      sessionId: string
      joinedCount: number
      requiredCount: number
      readyCount: number
      minDurationSec: number
      maxDurationSec: number
      topic?: string | null
      inviteCode: string
      sessionParticipants: Array<{ participantIndex: number; isReady: boolean }>
    }
  | {
      step: 'call'
      sessionId: string
      minDurationSec: number
      maxDurationSec: number
      callStartedAt?: string
      topic?: string | null
      inviteCode: string
    }
  | { step: 'ended'; durationSec: number }
  | { step: 'completed' }

interface StepSessionProps {
  projectId: string
  projectName: string
  rewardConfig?: {
    type: string
    amount: number
  }
  onBack: () => void
  onCompleted?: () => void
}

export function StepSession({ projectId, projectName, rewardConfig, onBack, onCompleted }: StepSessionProps) {
  const log = useConversationLogger()
  const [phase, setPhase] = useState<Phase>({ step: 'finding' })
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastSessionRef = useRef<string | null>(null)

  const {
    livekit,
    livekitToken,
    setLivekitToken,
    restart,
    setRestart,
    restartPending,
    setRestartPending,
    recordingStartsAt,
    setRecordingStartsAt,
    recordingStartsAtRef
  } = useSessionLiveKit()

  const [restartCount, setRestartCount] = useState(0)
  const [topicCollapsed, setTopicCollapsed] = useState(false)
  const [instructionsCollapsed, setInstructionsCollapsed] = useState(false)

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      log('session', 'Stopping poll interval')
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [log])

  const enterCall = useCallback(({ sessionId, token, session, task }: EnterCallSession) => {
    lastSessionRef.current = sessionId
    setLivekitToken(token)
    setPhase({
      step: 'call',
      sessionId,
      ...session,
      callStartedAt: session.callStartedAt ?? undefined,
      topic: task?.topic,
    })
  }, [])

  const enterLobby = useCallback(
    ({
      sessionId,
      token,
      session,
      task,
      sessionParticipants = []
    }: EnterCallSession & { sessionParticipants?: Array<{ participantIndex: number; isReady: boolean }> }) => {
      lastSessionRef.current = sessionId
      if (token) setLivekitToken(token)
      setPhase({
        step: 'lobby',
        sessionId,
        ...session,
        topic: task?.topic,
        sessionParticipants
      })
    },
    []
  )

  const doJoinSession = useCallback(
    async (
      sessionId: string,
      inviteCode: string,
      initialParticipants?: Array<{ participantIndex: number; isReady: boolean }>
    ) => {
      const data = await joinSession(sessionId)
      const enterCallSession = {
        sessionId: data.session.id,
        token: '',
        task: data.task
      }

      if (data.status === 'active') {
        // readyCount < requiredCount = lobby (not all ready). Otherwise call.
        if (data.session.readyCount < data.session.requiredCount) {
          enterLobby({
            ...enterCallSession,
            token: data.livekitToken,
            session: { ...data.session, joinedCount: currentSession?.joinedCount ?? 0, inviteCode },
            sessionParticipants: initialParticipants
          })
        } else {
          enterCall({
            ...enterCallSession,
            token: data.livekitToken,
            session: { ...data.session, joinedCount: currentSession?.joinedCount ?? 0, inviteCode }
          })
        }
      } else {
        // "waiting" — not all joined yet, enter lobby without token
        enterLobby({
          ...enterCallSession,
          session: {
            joinedCount: data.session.joinedCount ?? 0,
            requiredCount: data.session.requiredCount,
            readyCount: 0,
            minDurationSec: 0,
            maxDurationSec: 0,
            callStartedAt: null,
            inviteCode
          },
          sessionParticipants: initialParticipants
        })
      }
    },
    [enterLobby, enterCall]
  )

  const {
    data: sessions = [],
    isLoading: isLoadingSessions
    /*   error: sessionsError */
  } = useMyConversationSessions(projectId)
  const currentSession = sessions[0]

  const findAndJoin = useCallback(
    async (opts?: { skipEndingCheck?: boolean }) => {
      if (isLoadingSessions) return
      setPhase({ step: 'finding' })
      if (!currentSession) return

      const isEndingSession = ['ENDING', 'PROCESSING'].includes(currentSession.status)
      const isExistingSession = ['WAITING', 'ACTIVE'].includes(currentSession.status)

      if (isEndingSession && !opts?.skipEndingCheck) {
        setPhase({ step: 'ended', durationSec: 0 })
        return
      }

      // Check for WAITING/ACTIVE sessions — rejoin
      if (isExistingSession) {
        await doJoinSession(currentSession.id, currentSession.inviteCode, currentSession.participants)
        return
      }
    },
    [doJoinSession, currentSession, isLoadingSessions]
  )

  useEffect(() => {
    findAndJoin()
  }, [findAndJoin])

  const handleReady = useCallback(async () => {
    if (phase.step !== 'lobby') return

    const { sessionId } = phase
    log('api', `POST /sessions/${sessionId}/ready`)

    try {
      const data = await signalReady(sessionId)
      log('api', `Ready: allReady=${data.allReady}`)

      if (data.allReady) {
        if (data.recordingStartsAt) {
          setRecordingStartsAt(data.recordingStartsAt)
        }
      }
    } catch (err) {
      const msg = extractApiError(err, 'Failed to signal ready')
      log('error', msg, err)
      toast.error(msg)
    }
  }, [phase, log])

  const handleCountdownComplete = useCallback(() => {
    if (phase.step !== 'lobby') return
    log('session', 'Countdown complete, transitioning to call')
    stopPolling()
    setRecordingStartsAt(null)
    setPhase({
      step: 'call',
      sessionId: phase.sessionId,
      minDurationSec: phase.minDurationSec,
      maxDurationSec: phase.maxDurationSec,
      topic: phase.topic,
      inviteCode: phase.inviteCode
    })
  }, [phase, log, stopPolling])

  const handleLeaveLobby = useCallback(async () => {
    if (phase.step !== 'lobby') return

    const { sessionId } = phase
    log('api', `POST /sessions/${sessionId}/leave (lobby)`)

    try {
      await leaveSession(sessionId)
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status
      if (status !== 400) {
        toast.error(extractApiError(err, 'Failed to leave session'))
        return
      }
    }

    setLivekitToken('')
    stopPolling()
    onBack()
  }, [phase, log, stopPolling, onBack])

  const handleRequestRestart = useCallback(async () => {
    if (phase.step !== 'call' || restartPending) return

    const { sessionId } = phase
    log('api', `POST /sessions/${sessionId}/restart`)
    setRestartPending(true)

    try {
      const data = await requestRestart(sessionId)
      log('api', `Restart created: ${data.id}`, data)
      setRestart({
        activeRestartId: data.id,
        hasVoted: true,
        isRequester: true,
        requiredVotes: data.requiredVotes,
        yesCount: data.yesCount,
        noCount: data.noCount
      })
    } catch (err) {
      setRestartPending(false)
      const msg = extractApiError(err, 'Failed to request restart')
      log('error', msg, err)
      toast.error(msg)
    }
  }, [phase, restartPending, log])

  const handleVoteRestart = useCallback(
    async (vote: boolean) => {
      if (!restart.activeRestartId || phase.step !== 'call') return

      const { sessionId } = phase
      const { activeRestartId } = restart
      log('api', `POST /sessions/${sessionId}/restart/${activeRestartId}/vote (${vote})`)

      try {
        const data = await voteRestart(sessionId, activeRestartId, vote)
        log('api', `Vote result: ${data.status}`, data)
        setRestart((prev) => ({
          ...prev,
          hasVoted: true,
          yesCount: data.yesCount,
          noCount: data.noCount
        }))
      } catch (err) {
        const msg = extractApiError(err, 'Failed to vote')
        log('error', msg, err)
        toast.error(msg)
      }
    },
    [restart, phase, log]
  )

  const lobbySessionId = phase.step === 'lobby' ? phase.sessionId : null

  useEffect(() => {
    if (!lobbySessionId) {
      stopPolling()
      return
    }

    const sessionId = lobbySessionId
    log('session', `Starting lobby poll for ${sessionId} (every 3s)`)

    const poll = async () => {
      try {
        const status = await fetchSessionStatus(sessionId)

        if (status.status === 'CANCELLED') {
          stopPolling()
          setLivekitToken('')
          log('session', 'Session cancelled, re-entering matchmaking')
          toast.info(t`Your match didn't connect. Finding a new one...`)
          findAndJoin({ skipEndingCheck: true })
          return
        }

        if (status.status === 'FAILED') {
          stopPolling()
          setLivekitToken('')
          setError('Session failed. Please try again.')
          return
        }

        // Session ACTIVE but no token yet — re-join to get it
        if (status.status === 'ACTIVE' && !livekitToken) {
          log('session', 'Session ACTIVE but no token, re-joining')
          stopPolling()
          await doJoinSession(sessionId, status.inviteCode)
          return
        }

        // All participants ready — pick up recordingStartsAt for countdown
        if (status.status === 'ACTIVE' && status.readyCount >= status.requiredCount) {
          // Countdown fallback: always pick up recordingStartsAt before
          // checking callStartedAt, so the first-to-click-ready participant
          // sees the countdown even if the LiveKit data message was missed.
          if (status.recordingStartsAt && !recordingStartsAt) {
            log('session', 'Picked up recordingStartsAt from polling fallback')
            setRecordingStartsAt(status.recordingStartsAt)
            // Let the countdown timer handle the transition to call
            setPhase((prev) =>
              prev.step === 'lobby' ? { ...prev, readyCount: status.readyCount, isReady: true } : prev
            )
            return
          }

          // If callStartedAt is already set and countdown is finished,
          // recording has started — go to call. Don't transition while
          // countdown is still in progress (egress starts before countdown ends).
          if (status.callStartedAt && !recordingStartsAt) {
            log('session', 'All ready and recording started, transitioning to call')
            stopPolling()
            setPhase((prev) =>
              prev.step === 'lobby'
                ? {
                    step: 'call',
                    sessionId,
                    minDurationSec: status.minDurationSec,
                    maxDurationSec: status.maxDurationSec,
                    callStartedAt: status.callStartedAt ?? undefined,
                    topic: prev.topic,
                    inviteCode: prev.inviteCode
                  }
                : prev
            )
            return
          }

          setPhase((prev) => (prev.step === 'lobby' ? { ...prev, readyCount: status.readyCount, isReady: true } : prev))
          return
        }

        // Sync per-participant ready states from sessions/mine
        const sessionsResponse = await fetchMySessions(projectId)
        const mySession = sessionsResponse.data.find((s: { id: string }) => s.id === sessionId)
        if (mySession?.participants) {
          setPhase((prev) =>
            prev.step === 'lobby'
              ? {
                  ...prev,
                  joinedCount: mySession.joinedCount,
                  sessionParticipants: mySession.participants.map(
                    (sp: { participantIndex: number; isReady: boolean }) => ({
                      participantIndex: sp.participantIndex,
                      isReady: sp.isReady
                    })
                  )
                }
              : prev
          )
        }

        setPhase((prev) => {
          if (prev.step !== 'lobby') return prev
          return {
            ...prev,
            readyCount: status.readyCount,
            minDurationSec: status.minDurationSec || prev.minDurationSec,
            maxDurationSec: status.maxDurationSec || prev.maxDurationSec
          }
        })

        // Sync restart state from status (fallback for missed data messages)
        setRestartCount(status.restartCount)
      } catch (err) {
        log('error', `Lobby poll error: ${extractApiError(err, 'status check failed')}`, err)
      }
    }

    poll()
    const interval = setInterval(poll, 3000)
    pollRef.current = interval
    return () => {
      clearInterval(interval)
      if (pollRef.current === interval) pollRef.current = null
    }
  }, [lobbySessionId, livekitToken, stopPolling, log, doJoinSession, findAndJoin, recordingStartsAt])

  const callSessionId = phase.step === 'call' ? phase.sessionId : null

  useEffect(() => {
    if (!callSessionId) return

    const sessionId = callSessionId
    const interval = setInterval(async () => {
      try {
        const status = await fetchSessionStatus(sessionId)

        // Only sync restartCount when not in countdown (countdown handles its own reset)
        if (!recordingStartsAtRef.current) {
          setRestartCount(status.restartCount)
        }

        // Sync callStartedAt after a restart so the timer resets
        if (status.callStartedAt) {
          setPhase((prev) =>
            prev.step === 'call' && prev.callStartedAt !== status.callStartedAt
              ? {
                  ...prev,
                  callStartedAt: status.callStartedAt ?? undefined
                }
              : prev
          )
        }

        // Sync active restart request as fallback for missed data messages
        if (status.activeRestartRequest && !restart.activeRestartId) {
          const req = status.activeRestartRequest
          log('session', 'Detected active restart from status poll', req)
          setRestart({
            activeRestartId: req.id,
            hasVoted: false,
            isRequester: req.isRequester,
            requiredVotes: req.requiredVotes,
            yesCount: req.yesCount,
            noCount: req.noCount
          })
        } else if (!status.activeRestartRequest && restart.activeRestartId) {
          // Restart resolved but we missed the event
          setRestart(initialRestartState)
          setRestartPending(false)
        }
      } catch {
        // Ignore poll errors during call
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [callSessionId, restart.activeRestartId, log])

  const handleRestartCountdownComplete = useCallback(() => {
    log('session', 'Restart countdown complete, resetting timer')
    setRecordingStartsAt(null)
    setRestartCount((c) => c + 1)
    toast.success(t`Recording restarted`)
  }, [log])

  const handleCallEnded = useCallback(
    async (durationSec: number) => {
      log('phase', `Phase -> ended (duration: ${durationSec}s)`)
      setLivekitToken('')
      setRestart(initialRestartState)

      try {
        const result = await fetchSessionStatus(projectId)
        if (result.status === 'COMPLETED') {
          log('phase', `Phase -> completed (${result.status} after call)`)
          setPhase({ step: 'completed' })
          onCompleted?.()
          return
        }
      } catch {
        // If check fails, fall through to ended screen
      }

      setPhase({ step: 'ended', durationSec })
    },
    [log, projectId, onCompleted]
  )

  if (phase.step === 'finding' && !error) {
    return <WaitingForSessionStep onBack={onBack} />
  }

  if (error) {
    return <ErrorStep error={error} onBack={onBack} />
  }

  if (phase.step === 'lobby') {
    const localParticipantIndex = livekit.participants.find((p) => p.isLocal)?.participantIndex
    const localSessionParticipant = phase.sessionParticipants.find(
      (sp) => sp.participantIndex === localParticipantIndex
    )
    const lobbyIsReady = localSessionParticipant?.isReady || false

    return (
      <SessionLobby
        joinedCount={phase.joinedCount}
        requiredCount={phase.requiredCount}
        readyCount={phase.readyCount}
        isReady={lobbyIsReady}
        topic={phase.topic ?? undefined}
        instruction={currentSession?.task?.instruction ?? undefined}
        participantInstructions={currentSession?.task?.participantInstructions ?? []}
        onReady={handleReady}
        onLeave={handleLeaveLobby}
        connectionState={livekit.connectionState}
        participants={livekit.participants}
        isMuted={livekit.isMuted}
        toggleMute={livekit.toggleMute}
        hasLiveKit={!!livekitToken}
        recordingStartsAt={recordingStartsAt}
        onCountdownComplete={handleCountdownComplete}
        inviteCode={phase.inviteCode}
        sessionParticipants={phase.sessionParticipants}
        topicCollapsed={topicCollapsed}
        onTopicCollapsedChange={setTopicCollapsed}
        instructionsCollapsed={instructionsCollapsed}
        onInstructionsCollapsedChange={setInstructionsCollapsed}
      />
    )
  }

  if (phase.step === 'call') {
    return (
      <SessionCall
        sessionId={phase.sessionId}
        minDurationSec={phase.minDurationSec}
        maxDurationSec={phase.maxDurationSec}
        callStartedAt={phase.callStartedAt}
        topic={phase.topic ?? undefined}
        instruction={currentSession?.task?.instruction ?? undefined}
        participantInstructions={currentSession?.task?.participantInstructions ?? []}
        onEnded={handleCallEnded}
        connectionState={livekit.connectionState}
        participants={livekit.participants}
        isMuted={livekit.isMuted}
        toggleMute={livekit.toggleMute}
        restart={restart}
        restartCount={restartCount}
        restartPending={restartPending}
        onRequestRestart={handleRequestRestart}
        onVoteRestart={handleVoteRestart}
        recordingStartsAt={recordingStartsAt}
        onRestartCountdownComplete={handleRestartCountdownComplete}
        topicCollapsed={topicCollapsed}
        onTopicCollapsedChange={setTopicCollapsed}
        instructionsCollapsed={instructionsCollapsed}
        onInstructionsCollapsedChange={setInstructionsCollapsed}
      />
    )
  }

  if (phase.step === 'completed') {
    return (
      <NectarTasksAllDone
        name={projectName}
        rewardConfig={{ type: rewardConfig?.type ?? 'points', amount: rewardConfig?.amount ?? 0 }}
        projectId={projectId}
      />
    )
  }

  return (
    <ConversationCompleteStep
      phase={phase as Extract<Phase, { step: 'ended' }>}
      sessionId={currentSession?.id ?? ''}
      projectId={projectId}
      onBack={onBack}
    />
  )
}
